// Donors — everyone who has given in the session's scope (LE + location + department, and the
// ?cause= filter). Search, sort and paging happen on the server; each name opens the donor menu
// (Open / Call / WhatsApp / Email).

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileSpreadsheet, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

import { ApiError, saveBlob } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { downloadDonorsXlsx, fetchDonors, formatAmount, type DonorSort, type DonorsPage } from "@/lib/donation";
import { formatDate } from "@/lib/format";
import { useSessionScope } from "@/lib/session-scope";
import { cn } from "@/lib/utils";
import { PageContainer } from "@/components/page";
import { BlockPlaceholder, Busy } from "@/components/busy";
import { CauseFilter, useCauseParam } from "@/components/cause-filter";
import { MobileFilters } from "@/components/filter-sheet";
import { DonorLink } from "@/components/donor-link";
import { EmailValue, PhoneValue } from "@/components/contact-value";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SORTS: { value: DonorSort; label: string }[] = [
  { value: "fyAmount", label: "Most given this FY" },
  { value: "lifetimeAmount", label: "Most given overall" },
  { value: "lastDonation", label: "Most recent donation" },
  { value: "name", label: "Name (A–Z)" },
];

export default function Donors() {
  const { token } = useAuth();
  const { scope } = useSessionScope();
  const [params, setParams] = useSearchParams();
  const [causeId] = useCauseParam();

  const search = params.get("q") ?? "";
  const sort = (SORTS.some((s) => s.value === params.get("sort")) ? params.get("sort") : "fyAmount") as DonorSort;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const budgetYearId = Number(params.get("fy")) || null;

  // The box types freely; the request waits until typing pauses.
  const [query, setQuery] = useState(search);
  const [data, setData] = useState<DonorsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  /** Excel export is built on the server, with the same search / sort (all rows). */
  async function exportExcel() {
    if (!token || !scope || !data || data.total === 0) return;
    setExporting(true);
    try {
      const { blob, fileName } = await downloadDonorsXlsx(token, scope, { budgetYearId, causeId, search, sort });
      saveBlob(blob, fileName);
    } catch (e) {
      toast.error(`Could not export: ${(e as Error).message}`);
    } finally {
      setExporting(false);
    }
  }

  function update(next: Record<string, string | null>) {
    const q = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v == null || v === "" ? q.delete(k) : q.set(k, v)));
    setParams(q, { replace: true });
  }

  useEffect(() => {
    const t = setTimeout(() => {
      if (query !== search) update({ q: query, page: null });
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (!token || !scope) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchDonors(token, scope, { budgetYearId, causeId, search, sort, page }, controller.signal)
      .then(setData)
      .catch((e) => {
        if ((e as Error).name === "AbortError") return;
        setError(
          e instanceof ApiError && e.status === 403
            ? 'The donors list needs the "View donation" permission.'
            : (e as Error).message
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [token, scope, budgetYearId, causeId, search, sort, page]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  // Rendered inline on desktop and inside the phone filter sheet — one definition, two places.
  const filterControls = (
    <>
      <div className="w-full md:w-52">
        <Select items={SORTS} value={sort} onValueChange={(v) => v && update({ sort: String(v), page: null })}>
          <SelectTrigger className="w-full" aria-label="Sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <CauseFilter className="w-full md:w-56" />
    </>
  );
  const activeFilterCount = (sort !== "fyAmount" ? 1 : 0) + (causeId ? 1 : 0);

  return (
    <PageContainer full fill>
      {/* The page fills the screen and only the table scrolls, so the filters, the column
          header and the pager stay put. On a phone the filters live behind a button, which keeps
          this strip to one row. */}
      <div className="flex shrink-0 flex-col gap-3">
        <div className="flex items-end gap-3">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or mobile…"
              className="pl-8"
            />
          </div>
          {/* md and up: inline */}
          <div className="hidden items-end gap-3 md:flex">{filterControls}</div>
          {/* phone: behind the button */}
          <div className="md:hidden">
            <MobileFilters activeCount={activeFilterCount}>{filterControls}</MobileFilters>
          </div>
          <Button
            variant="outline"
            onClick={exportExcel}
            disabled={!data || data.total === 0 || exporting}
            className="shrink-0"
          >
            {exporting ? <Loader2 className="animate-spin" /> : <FileSpreadsheet />}
            <span className="hidden sm:inline">Export to Excel</span>
          </Button>
        </div>

        <p className="text-sm text-muted-foreground">
          {scope?.operationUnit.name}
          {scope?.department ? ` · ${scope.department.name}` : ""} · {scope?.legalEntity.name}
          {data && (
            <>
              {" · "}
              {data.total.toLocaleString("en-IN")} {data.total === 1 ? "donor" : "donors"}
              {data.total > data.pageSize ? ` · page ${data.page} of ${totalPages}` : ""}
            </>
          )}
        </p>
      </div>

      {error ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-destructive">{error}</CardContent>
        </Card>
      ) : !data ? (
        <BlockPlaceholder className="h-96" />
      ) : (
        <Busy busy={loading} fill className="flex min-h-0 flex-1 flex-col">
          <Card className="flex min-h-0 flex-1 flex-col py-0">
            <CardContent className="min-h-0 flex-1 p-0">
              {data.rows.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">
                  {search ? "No donor matches this search." : "No donors in this scope yet."}
                </p>
              ) : (
                <div className="h-full overflow-auto">
                  <table className="w-full text-sm">
                    {/* Opaque background: rows scroll underneath this row. */}
                    <thead className="sticky top-0 z-10 bg-muted">
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Donor</th>
                        <th className="px-3 py-2 font-medium">Mobile</th>
                        <th className="px-3 py-2 font-medium">Email</th>
                        <th className="px-3 py-2 text-right font-medium whitespace-nowrap">
                          FY {data.budgetYear.name}
                        </th>
                        <th className="px-3 py-2 text-right font-medium whitespace-nowrap">All years</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Last donation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r) => (
                        <tr key={r.donorId} className="border-b border-border/60 last:border-0">
                          <td className="px-3 py-2">
                            <DonorLink
                              name={r.name || "(unnamed)"}
                              donorId={r.donorId}
                            />
                            {r.notes && <div className="text-xs text-amber-700 dark:text-amber-400">{r.notes}</div>}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <PhoneValue value={r.mobile} />
                          </td>
                          <td className="max-w-56 truncate px-3 py-2">
                            <EmailValue value={r.email} />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                            {formatAmount(r.fyAmount)}
                            <span className="ml-1 text-xs text-muted-foreground">({r.fyCount})</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                            {formatAmount(r.lifetimeAmount)}
                            <span className="ml-1 text-xs text-muted-foreground">({r.lifetimeCount})</span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.lastDonation)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </Busy>
      )}

      {data && totalPages > 1 && (
        <div className="flex shrink-0 items-center justify-center gap-2 pb-1">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => update({ page: String(page - 1) })}
          >
            <ChevronLeft /> Previous
          </Button>
          <span className={cn("text-sm text-muted-foreground")}>
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => update({ page: String(page + 1) })}
          >
            Next <ChevronRight />
          </Button>
        </div>
      )}
    </PageContainer>
  );
}
